import { creditReasonMessage } from './tenderWording.js';

/**
 * 358 (#1091, FR-007, FR-014): "Use my credit" at checkout. One tap draws the customer's credit across their
 * certificates (the platform chooses the order: soonest expiry first); one tap removes it. Like a credit code, it is
 * a tender: never with a voucher and never with a code typed beside it, so the step steps aside for either.
 */
export interface CreditSummary {
  verified: boolean;
  total_minor: number;
}

/** The quote's `credit` block, plus the amount still to pay. */
export interface CreditQuoteInfo {
  applied: boolean;
  reason?: string;
  credit_minor?: number;
  amount_due_minor?: number;
}

interface CreditStepProps {
  /** `GET /credit`, or null until it answers (or for a guest). */
  credit: CreditSummary | null;
  applied: boolean;
  onChange: (applied: boolean) => void;
  isVoucherApplied?: boolean;
  isCodeEntered?: boolean;
  quote?: CreditQuoteInfo;
}

const rm = (minor: number) => `RM ${(minor / 100).toFixed(2)}`;

export function CreditStep({
  credit,
  applied,
  onChange,
  isVoucherApplied,
  isCodeEntered,
  quote,
}: CreditStepProps) {
  // Nothing to offer: no read yet, no verified email, or no credit. The account page explains each of these.
  if (!credit?.verified || !(credit.total_minor > 0)) return null;

  if (isVoucherApplied || isCodeEntered) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm text-gray-400">Credit</p>
        <p data-testid="credit-blocked" className="text-xs text-gray-500">
          {isVoucherApplied
            ? 'A voucher is applied. Remove it to use your credit instead.'
            : 'A credit code is entered. Remove it to use your credit instead.'}
        </p>
      </div>
    );
  }

  if (!applied) {
    return (
      <button
        type="button"
        onClick={() => onChange(true)}
        className="rounded-full bg-secondary px-4 py-3 text-sm font-semibold text-on-secondary"
      >
        {`Use my credit (${rm(credit.total_minor)} available)`}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-surface-card p-3">
      {quote?.applied && quote.credit_minor !== undefined ? (
        <p data-testid="credit-applied" className="flex justify-between text-sm text-green-400">
          <span>Credit applied</span>
          <span>−{rm(quote.credit_minor)}</span>
        </p>
      ) : null}
      {quote?.applied && quote.amount_due_minor !== undefined ? (
        <p
          data-testid="credit-due"
          className="flex justify-between text-sm font-semibold text-text"
        >
          <span>Amount due</span>
          <span>{rm(quote.amount_due_minor)}</span>
        </p>
      ) : null}
      {quote && !quote.applied ? (
        <p data-testid="credit-reason" className="text-sm text-red-400">
          {creditReasonMessage(quote.reason)}
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => onChange(false)}
        className="self-start text-sm text-gray-400 underline"
      >
        Remove
      </button>
    </div>
  );
}
